// ObjectControls — transform panel for content objects, AI worlds, screen props
// Has Move/Rotate/Scale toggle bar like PrimitiveControls
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Unlock, RotateCcw, X, Trash2, Copy, Move, RotateCw, Maximize2 } from 'lucide-react';
import { useDraggable } from '../hooks/useDraggable';
import './ObjectControls.css';
import PBRBrowser from './PBRBrowser';

interface ObjectControlsProps {
  name: string;
  position: [number, number, number];
  scale: number | [number, number, number];
  rotation: [number, number, number];
  isLocked: boolean;
  transformMode: 'translate' | 'rotate' | 'scale';
  onTransformModeChange: (mode: 'translate' | 'rotate' | 'scale') => void;
  onPositionChange: (position: [number, number, number]) => void;
  onScaleChange: (scale: number) => void;
  onRotationChange: (rotation: [number, number, number]) => void;
  onLockToggle: () => void;
  onReset: () => void;
  onClose: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  collidable?: boolean;
  onCollidableChange?: (collidable: boolean) => void;
  seat?: boolean;
  onSeatChange?: (seat: boolean) => void;
  environmentMesh?: boolean;
  onEnvironmentMeshChange?: (on: boolean) => void;
  // Material slots exposed by the loaded model, plus current overrides.
  // Recolouring a slot repaints every piece using it.
  materialSlots?: { name: string; color: string }[];
  materialColors?: Record<string, string>;
  onMaterialColorChange?: (slot: string, color: string | null) => void;
  materialTextures?: Record<string, { maps: any; scale?: number }>;
  onMaterialTextureChange?: (slot: string, value: { maps: any; scale?: number } | null) => void;
}

const ObjectControls: React.FC<ObjectControlsProps> = ({
  name,
  position,
  scale,
  rotation,
  isLocked,
  transformMode,
  onTransformModeChange,
  onPositionChange,
  onScaleChange,
  onRotationChange,
  onLockToggle,
  onReset,
  onClose,
  onDelete,
  onDuplicate,
  collidable,
  onCollidableChange,
  seat,
  environmentMesh,
  onEnvironmentMeshChange,
  materialSlots,
  materialColors,
  onMaterialColorChange,
  materialTextures,
  onMaterialTextureChange,
  onSeatChange,
}) => {
  // which slot the texture library is open for
  const [texSlot, setTexSlot] = useState<string | null>(null);
  const { dragStyle, onPointerDown: onDragPointerDown } = useDraggable();
  const panelRef = useRef<HTMLDivElement>(null);
  const scaleVal = typeof scale === 'number' ? scale : scale[0];

  // Block events so OrbitControls doesn't steal pointer
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'wheel', 'touchstart', 'touchmove', 'touchend'];
    events.forEach((evt) => el.addEventListener(evt, stop, { capture: true }));
    return () => { events.forEach((evt) => el.removeEventListener(evt, stop, { capture: true })); };
  }, []);

  const handlePositionChange = (axis: number, value: number) => {
    if (isLocked) return;
    const newPos: [number, number, number] = [...position];
    newPos[axis] = value;
    onPositionChange(newPos);
  };

  const handleRotationChange = (axis: number, value: number) => {
    if (isLocked) return;
    const newRot: [number, number, number] = [...rotation];
    newRot[axis] = value;
    onRotationChange(newRot);
  };

  return (
    <div ref={panelRef} className="object-controls glassmorphism-dark" style={dragStyle} onPointerDown={(e) => { e.stopPropagation(); onDragPointerDown(e); }}>
      <div className="controls-header" data-drag-handle>
        <h3>{name}</h3>
        <div className="header-buttons">
          <button
            className={`icon-btn ${isLocked ? 'locked' : ''}`}
            onClick={onLockToggle}
            title={isLocked ? 'Unlock' : 'Lock'}
          >
            {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
          </button>
          <button className="icon-btn" onClick={onClose} title="Close"><X size={16} /></button>
        </div>
      </div>

      {/* Transform mode toggle */}
      <div className="transform-mode-toggle">
        {([
          { mode: 'translate' as const, icon: <Move size={14} />, label: 'Move' },
          { mode: 'rotate' as const, icon: <RotateCw size={14} />, label: 'Rotate' },
          { mode: 'scale' as const, icon: <Maximize2 size={14} />, label: 'Scale' },
        ]).map(({ mode, icon, label }) => (
          <button
            key={mode}
            className={`transform-mode-btn ${transformMode === mode ? 'active' : ''}`}
            onClick={() => onTransformModeChange(mode)}
            title={label}
            disabled={isLocked}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className={`controls-content ${isLocked ? 'disabled' : ''}`}>
        {/* Position */}
        <div className="control-group">
          <label>Position</label>
          {(['X', 'Y', 'Z'] as const).map((axis, i) => (
            <div className="control-row" key={axis}>
              <span>{axis}:</span>
              <input
                type="range" min={-50} max={50} step={0.1}
                value={position[i]}
                onChange={(e) => handlePositionChange(i, parseFloat(e.target.value))}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={parseFloat(position[i].toFixed(1))}
                onChange={(e) => handlePositionChange(i, parseFloat(e.target.value) || 0)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.1}
                disabled={isLocked}
              />
            </div>
          ))}
        </div>

        {/* Scale (uniform) */}
        <div className="control-group">
          <label>Scale</label>
          <div className="control-row">
            <input
              type="range" min={0.01} max={20} step={0.01}
              value={scaleVal}
              onChange={(e) => onScaleChange(parseFloat(e.target.value))}
              disabled={isLocked}
            />
            <input
              type="number"
              className="value-input"
              value={parseFloat(scaleVal.toFixed(2))}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v) && v > 0) onScaleChange(v);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              step={0.01}
              min={0.01}
              disabled={isLocked}
            />
          </div>
        </div>

        {/* Rotation */}
        <div className="control-group">
          <label>Rotation</label>
          {(['X', 'Y', 'Z'] as const).map((axis, i) => {
            const deg = Math.round(rotation[i] * 180 / Math.PI);
            return (
              <div className="control-row" key={axis}>
                <span>{axis}:</span>
                <input
                  type="range" min={-180} max={180} step={1}
                  value={deg}
                  onChange={(e) => handleRotationChange(i, parseFloat(e.target.value) * Math.PI / 180)}
                  disabled={isLocked}
                />
                <input
                  type="number"
                  className="value-input"
                  value={deg}
                  onChange={(e) => handleRotationChange(i, (parseFloat(e.target.value) || 0) * Math.PI / 180)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  step={1}
                  disabled={isLocked}
                />
                <span style={{ color: '#888', fontSize: 11 }}>&deg;</span>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="object-actions">
          {onDuplicate && (
            <button className="action-btn duplicate-btn" onClick={onDuplicate} disabled={isLocked}>
              <Copy size={14} /> Duplicate
            </button>
          )}
          {onDelete && (
            <button className="action-btn delete-btn" onClick={onDelete} disabled={isLocked}>
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button className="reset-btn" onClick={onReset} disabled={isLocked}>
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      {onCollidableChange && (
        <div className="control-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={!!collidable} onChange={(e) => onCollidableChange(e.target.checked)} disabled={isLocked} style={{ accentColor: '#00c2b3' }} />
            Solid (players collide with it)
          </label>
        </div>
      )}
      {onSeatChange && (
        <div className="control-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={!!seat} onChange={(e) => onSeatChange(e.target.checked)} disabled={isLocked} style={{ accentColor: '#00c2b3' }} />
            🪑 Place to sit (players can sit here)
          </label>
        </div>
      )}
      {onEnvironmentMeshChange && (
        <div className="control-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={!!environmentMesh} onChange={(e) => onEnvironmentMeshChange(e.target.checked)} disabled={isLocked} style={{ accentColor: '#00c2b3' }} />
            🌅 Use as sky (unlit, seen from inside)
          </label>
          {environmentMesh && (
            <div style={{ fontSize: 10.5, color: 'rgba(234,246,248,0.5)', marginTop: 4 }}>
              Sized to enclose the world and centred on the origin. Scale it to taste — you're standing inside it.
            </div>
          )}
        </div>
      )}
      {onMaterialColorChange && !!materialSlots?.length && (
        <div className="control-group">
          <label>Colours by element</label>
          <div style={{ fontSize: 10.5, color: 'rgba(234,246,248,0.5)', margin: '2px 0 8px' }}>
            Each swatch paints every piece using that material — recolour one seat and they all change.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {materialSlots.map((slot) => {
              const current = materialColors?.[slot.name] || slot.color;
              const overridden = !!materialColors?.[slot.name];
              const textured = !!materialTextures?.[slot.name];
              return (
                <div key={slot.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="color"
                    value={current}
                    disabled={isLocked}
                    onChange={(e) => onMaterialColorChange(slot.name, e.target.value)}
                    style={{ width: 26, height: 22, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 11, color: 'rgba(234,246,248,0.8)', textTransform: 'capitalize', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {slot.name}
                  </span>
                  {onMaterialTextureChange && (
                    <button
                      onClick={() => setTexSlot(slot.name)}
                      title={textured ? 'Change this element\'s material' : 'Apply a material from the texture library'}
                      style={{
                        background: textured ? 'rgba(0,194,179,0.2)' : 'none',
                        border: '1px solid rgba(0,194,179,0.35)', borderRadius: 4,
                        color: textured ? '#3fecdb' : 'rgba(234,246,248,0.5)',
                        cursor: 'pointer', fontSize: 10, padding: '1px 4px',
                      }}
                    >tex</button>
                  )}
                  {(overridden || textured) && (
                    <button
                      onClick={() => {
                        if (overridden) onMaterialColorChange(slot.name, null);
                        if (textured && onMaterialTextureChange) onMaterialTextureChange(slot.name, null);
                      }}
                      title="Reset this element to the model's original look"
                      style={{ background: 'none', border: 'none', color: 'rgba(234,246,248,0.45)', cursor: 'pointer', fontSize: 12, padding: 0 }}
                    >×</button>
                  )}
                </div>
              );
            })}
          </div>
          {!!materialTextures && Object.keys(materialTextures).length > 0 && onMaterialTextureChange && (
            <div style={{ marginTop: 8 }}>
              {Object.entries(materialTextures).map(([name, val]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <span style={{ fontSize: 10.5, color: 'rgba(234,246,248,0.6)', width: 64, textTransform: 'capitalize' }}>{name} tile</span>
                  <input
                    type="range" min={0.1} max={3} step={0.05}
                    value={val.scale ?? 0.5}
                    disabled={isLocked}
                    onChange={(e) => onMaterialTextureChange(name, { ...val, scale: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 10, color: 'rgba(234,246,248,0.5)', width: 26 }}>{(val.scale ?? 0.5).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {texSlot && onMaterialTextureChange && createPortal(
        <PBRBrowser
          onApply={(maps) => {
            onMaterialTextureChange(texSlot, { maps, scale: materialTextures?.[texSlot]?.scale ?? 0.5 });
            setTexSlot(null);
          }}
          onClose={() => setTexSlot(null)}
        />, document.body)}
      </div>
    </div>
  );
};

export default ObjectControls;
